/**
 * ConversationStatusBadge
 * Badge + dropdown per cambiare lo stato della conversazione
 */

import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

type Status = 'da_gestire' | 'in_gestione' | 'chiusa';

const STATUS_CONFIG: Record<Status, { label: string; color: string; bg: string; dot: string }> = {
  da_gestire: { label: 'Da gestire', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800', dot: 'bg-orange-500' },
  in_gestione: { label: 'In gestione', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800', dot: 'bg-blue-500' },
  chiusa: { label: 'Chiusa', color: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700', dot: 'bg-gray-400' },
};

interface Props {
  status: Status;
  onChange: (status: Status) => void;
}

export function ConversationStatusBadge({ status, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cfg = STATUS_CONFIG[status];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${cfg.bg} ${cfg.color}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
        {cfg.label}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 py-1 min-w-[140px]">
          {(Object.entries(STATUS_CONFIG) as [Status, typeof STATUS_CONFIG[Status]][]).map(([key, c]) => (
            <button
              key={key}
              onClick={() => { onChange(key); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8 ${status === key ? 'font-semibold' : ''}`}
            >
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`} />
              <span className={c.color}>{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
